import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import * as yup from "yup";
import { Formik, Form, useField } from "formik";
import type { AppDispatch, RootState } from "../store/store.tsx";
import Button from "../components/common/Button.tsx";
import { useAuth } from "../../src/context/AuthContext.tsx";
import { decryptData } from "../storageHelper.ts";

interface LoginFormValues {
  email: string;
  password: string;
}

const EmailInput: React.FC = () => {
  const [field, meta] = useField("email");
  return (
    <div className="flex flex-col">
      <div className="flex items-center border-b border-white gap-3 py-2">
        <img src="/login/svg/mail.svg" alt="Email Icon" className="w-5 h-5" />
        <input
          {...field}
          type="email"
          placeholder="Mail ID"
          className="flex-1 py-2 bg-transparent text-white placeholder-white/70 text-sm outline-none"
        />
      </div>
      {meta.touched && meta.error && (
        <div className="text-red-400 text-xs mt-1">{meta.error}</div>
      )}
    </div>
  );
};

const PasswordInput: React.FC<{ showPassword: boolean; toggleShow: () => void }> = ({
  showPassword,
  toggleShow,
}) => {
  const [field, meta] = useField("password");
  return (
    <div className="flex flex-col">
      <div className="flex items-center border-b border-white gap-3 py-2">
        <img src="/login/svg/lock.svg" alt="Password Icon" className="w-5 h-5" />
        <input
          {...field}
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          className="flex-1 py-2 bg-transparent text-white placeholder-white/70 text-sm outline-none"
        />
        <img
          src={showPassword ? "/login/svg/eye_off.svg" : "/login/svg/eye_on.svg"}
          alt="Toggle password"
          className="w-5 h-5 opacity-50 cursor-pointer"
          onClick={toggleShow}
        />
      </div>
      {meta.touched && meta.error && (
        <div className="text-red-400 text-xs mt-1">{meta.error}</div>
      )}
      <div className="text-right mt-1">
        <Link to="/forgetpassword" className="text-white text-sm opacity-50 hover:underline">
          Forgot Password?
        </Link>
      </div>
    </div>
  );
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = yup.object({
    email: yup.string().email("Invalid Email Id").required("Email is Required"),
    password: yup.string().required("Password is Required"),
  });

  const { roleName } = useSelector((state: RootState) => state.userRegister);
  const initialValues: LoginFormValues = { email: "", password: "" };

  const onSubmit = async (values: LoginFormValues, { setSubmitting }: any) => {
    try {
      await login(values);
      message.success("Successfully Logged In");
    } catch (error: any) {
      if (error.response?.status === 401) {
        message.error("Invalid credentials");
      } else if (error.message === "Invalid token received from server") {
        message.error("Authentication failed");
      } else {
        message.error(error.response?.data?.message || "Something went wrong");
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const encryptedToken = localStorage.getItem("token");
    const token = decryptData(encryptedToken);
    if (token) {
      navigate("/dashboard", { replace: true, state: { roleName: roleName } });
    }
  }, [navigate, roleName]);

  return (
    <div
      className="flex flex-col md:flex-row h-screen overflow-hidden bg-[#001F56] bg-cover bg-center"
      style={{ backgroundImage: "url(/login/loginbglayer.png)" }}
    >
      {/* Left Section */}
      <div className="w-full md:w-1/2 flex items-center justify-center px-4 sm:px-6 md:px-12 bg-opacity-10 backdrop-blur-md">
        <div className="flex flex-col gap-6 w-full max-w-sm">
          <div className="flex flex-col items-center gap-8">
            <img src="/login/svg/logo.svg" alt="Logo" className="w-48" />
            <h1 className="text-3xl font-semibold text-white text-center">Login</h1>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-6 w-full">
                <EmailInput />
                <PasswordInput showPassword={showPassword} toggleShow={() => setShowPassword(p => !p)} />
                <Button
                  type="submit"
                  label={isSubmitting ? "Logging in..." : "LOGIN"}
                  className={`py-2 rounded-md font-semibold tracking-wide ${
                    isSubmitting
                      ? "bg-blue-500/40 text-white opacity-70 cursor-not-allowed"
                      : "bg-[#0071CD] text-white hover:bg-[#005fa3]"
                  }`}
                  disabled={isSubmitting}
                  onClick={() => {}}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Right Section */}
      <div className="relative hidden md:flex md:w-1/2 items-center justify-center">
        <div className="relative w-full h-full">
          <img
            src="/login/bluelayer.png"
            alt="Background Layer"
            className="absolute bottom-0 right-0 object-contain z-10"
            width={520}
            height={520}
          />
          <img
            src="/login/userimage.png"
            alt="Side Image"
            className="object-contain absolute bottom-0 right-20 z-20"
            width={520}
            height={520}
          />
          <img
            src="/login/plant.png"
            alt="Plant"
            className="absolute bottom-[-40px] right-0 transform -translate-y-1/2 object-contain z-30"
            width={250}
            height={250}
          />
          <img
            src="/login/light.png"
            alt="Light"
            className="absolute top-10 right-75 transform -translate-y-1/2 object-contain z-30"
            width={200}
            height={200}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
